import React, { useState, useEffect } from "react";
import {
  Box,
  Tabs,
  Text,
  Group,
  Button,
  Paper,
  SimpleGrid,
  Card,
  Image,
  Badge,
  Stack,
  ActionIcon,
  LoadingOverlay,
  Center,
  Pagination,
  Modal,
} from "@mantine/core";
import {
  IconVideo,
  IconPhoto,
  IconPlus,
  IconEye,
  IconTrash,
  IconCategory,
} from "@tabler/icons-react";
import { useNotification } from "../adminProjectNotifications/popupnotifications";
import ContentUploader from "./ContentUploader";
import { API_BASE_URL } from "../../config/baseUrls";

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
  "Content-Type": "application/json",
});

export default function FeedsManagement() {
  const [activeTab, setActiveTab] = useState("reels");

  return (
    <Box maxW={1200} mx="auto" pb={50} className="animate-fade-in">
      <Group justify="space-between" align="end" mb="xl">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Feeds & Content Management
          </Text>
          <Text size="sm" c="dimmed">
            Manage your native app Reels, Carousels, and image feeds directly.
          </Text>
        </Box>
      </Group>

      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        variant="outline"
        radius="md"
      >
        <Tabs.List>
          <Tabs.Tab value="reels" leftSection={<IconVideo size={16} />}>
            Reels Management
          </Tabs.Tab>
          <Tabs.Tab value="feeds" leftSection={<IconPhoto size={16} />}>
            Static Feeds
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="reels" pt="xl">
          <ContentGridManager contentType="reels" />
        </Tabs.Panel>

        <Tabs.Panel value="feeds" pt="xl">
          <ContentGridManager contentType="feeds" />
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

// ============================================================================
// SUB-COMPONENT: CONTENT GRID MANAGER (Handles both Reels & Feeds)
// ============================================================================
function ContentGridManager({ contentType }) {
  const { showError, showSuccess } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
  });

  const isReel = contentType === "reels";

  const fetchContent = async (page = 1) => {
    setLoading(true);
    try {
      // Calls the PaginatedContent controller you provided in strapiController.js
      const res = await fetch(
        `${API_BASE_URL}/content/paginatedContent?data=${contentType}&paginate=true&page=${page}`,
        { headers: getAuthHeaders() },
      );
      const result = await res.json();

      if (result.success) {
        setItems(result.data);
        if (result.pagination) {
          setPagination({
            page: result.pagination.currentPage,
            totalPages: result.pagination.totalPages,
          });
        }
      }
    } catch (error) {
      showError("Error", `Failed to load ${contentType}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent(1);
  }, [contentType]);

  // Handle successful upload from the modal
  const handleUploadComplete = () => {
    setUploadModalOpen(false);
    fetchContent(1); // Refresh the grid to show the new item!
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this content?"))
      return;

    try {
      // Assuming you create a DELETE route mapped to your schema
      const res = await fetch(
        `${API_BASE_URL}/api/admin/content/${contentType}/${documentId}`,
        {
          method: "DELETE",
          headers: getAuthHeaders(),
        },
      );

      if (res.ok) {
        showSuccess("Deleted", "Content removed successfully.");
        fetchContent(pagination.page);
      } else {
        showError("Error", "Failed to delete content.");
      }
    } catch (e) {
      showError("Network Error", "Failed to communicate with server.");
    }
  };

  return (
    <Box pos="relative" mih={400}>
      <LoadingOverlay visible={loading} overlayProps={{ blur: 1 }} />

      <Group justify="space-between" mb="md">
        <Text fw={600} size="lg">
          Live {isReel ? "Video Reels" : "Static Feeds"}
        </Text>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setUploadModalOpen(true)}
          className="btn-ota-primary"
        >
          Add New {isReel ? "Reel" : "Feed"}
        </Button>
      </Group>

      {items.length === 0 && !loading ? (
        <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
          <Text fw={600} size="lg" c="dimmed">
            No {contentType} found.
          </Text>
          <Text size="sm" c="dimmed" mb="md">
            Click "Add New" to ingest content from Instagram.
          </Text>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing="lg">
          {items.map((item) => (
            <Card
              key={item.documentId}
              p="0"
              radius="md"
              withBorder
              className="group"
            >
              {/* Media Thumbnail */}
              <Box pos="relative" h={240} bg="gray.1">
                {item.thumbnail ? (
                  <Image src={item.thumbnail} w="100%" h="100%" fit="cover" />
                ) : (
                  <Center h="100%">
                    {isReel ? (
                      <IconVideo size={30} color="#adb5bd" />
                    ) : (
                      <IconPhoto size={30} color="#adb5bd" />
                    )}
                  </Center>
                )}

                {/* Top Badges */}
                <Group
                  pos="absolute"
                  top={10}
                  left={10}
                  right={10}
                  justify="space-between"
                >
                  <Badge
                    color="dark"
                    variant="filled"
                    size="sm"
                    leftSection={<IconCategory size={10} />}
                  >
                    {item.category || "General"}
                  </Badge>
                  <ActionIcon
                    color="red"
                    variant="filled"
                    disabled={true}
                    size="sm"
                    radius="xl"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleDelete(item.documentId)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Group>

                {/* View Count Overlay */}
                <Box
                  pos="absolute"
                  bottom={10}
                  left={10}
                  bg="rgba(0,0,0,0.6)"
                  px={8}
                  py={4}
                  style={{ borderRadius: 12 }}
                >
                  <Group gap={4}>
                    <IconEye size={12} color="white" />
                    <Text size="xs" fw={700} c="white">
                      {item.viewCount?.toLocaleString() || 0}
                    </Text>
                  </Group>
                </Box>
              </Box>

              {/* Content Metadata */}
              <Stack p="md" gap="xs">
                <Text
                  size="sm"
                  fw={600}
                  lineClamp={2}
                  style={{ minHeight: 40 }}
                >
                  {item.caption || "No caption provided."}
                </Text>
                <Group justify="space-between">
                  <Text size="xs" c="dimmed" fw={500}>
                    {item.creator_tag}
                  </Text>
                  <Badge
                    size="xs"
                    variant="light"
                    color={isReel ? "pink" : "blue"}
                  >
                    {isReel ? "Reel" : "Feed"}
                  </Badge>
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* PAGINATION */}
      {pagination.totalPages > 1 && (
        <Center mt="xl">
          <Pagination
            total={pagination.totalPages}
            value={pagination.page}
            onChange={(p) => fetchContent(p)}
          />
        </Center>
      )}

      {/* UPLOAD MODAL */}
      <Modal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        title={
          <Text fw={800} size="lg">
            Ingest New Content
          </Text>
        }
        size="lg"
      >
        {/* Pass down the success handler to the Uploader so it closes the modal when done! */}
        <ContentUploader
          defaultType={isReel ? "Reels" : "Feeds"}
          onSuccess={handleUploadComplete}
        />
      </Modal>
    </Box>
  );
}
